import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function CoachSubscriptions() {
  const { profile } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [filter, setFilter] = useState('active');
  const [editingBilling, setEditingBilling] = useState(null);
  const [newBillingDate, setNewBillingDate] = useState('');

  useEffect(() => {
    if (profile?.id) fetchSubscriptions();
  }, [profile]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      // Fetch subscriptions with product and client info
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('*, client:client_id(full_name, email)')
        .eq('coach_id', profile.id)
        .order('created_at', { ascending: false });

      if (!subs || subs.length === 0) {
        setSubscriptions([]);
        setLoading(false);
        return;
      }

      // Fetch products separately for each subscription
      const productIds = [...new Set(subs.filter(s => s.product_id).map(s => s.product_id))];
      let products = [];
      if (productIds.length > 0) {
        const { data: productData } = await supabase
          .from('products')
          .select('id, name, price_amount, billing_interval, billing_type, currency')
          .in('id', productIds);
        products = productData || [];
      }

      // Merge products into subscriptions
      const merged = subs.map(sub => ({
        ...sub,
        product: products.find(p => p.id === sub.product_id) || null
      }));

      setSubscriptions(merged);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
  };

  // Calculate next billing date based on interval
  const getNextBillingDate = (currentEnd, interval) => {
    const date = new Date(currentEnd);
    switch (interval) {
      case 'week': date.setDate(date.getDate() + 7); break;
      case 'biweek': date.setDate(date.getDate() + 14); break;
      case 'month': date.setMonth(date.getMonth() + 1); break;
      case 'year': date.setFullYear(date.getFullYear() + 1); break;
      default: date.setMonth(date.getMonth() + 1);
    }
    return date;
  };

  const callApi = async (url, token, body = null) => {
    const opts = {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
    };
    if (body) opts.body = JSON.stringify(body);
    const res = await fetch(url, opts);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Request failed (${res.status})`);
    }
    return res.json();
  };

  const handleAction = async (sub, action) => {
    const messages = {
      cancel: 'Cancel this subscription? The client will lose access at the end of their billing period.',
      pause: 'Pause billing for this client? They keep access but billing stops.',
      skip: 'Skip the next payment for this client? The next billing date will be pushed forward one interval.'
    };
    if (!window.confirm(messages[action])) return;
    setActioning(sub.id + '_' + action);
    try {
      const token = await getToken();
      const hasRealStripeId = sub.stripe_subscription_id && !sub.stripe_subscription_id.startsWith('sub_test_');

      if (hasRealStripeId) {
        // Backend calls Stripe first, then updates Supabase — throws on Stripe error
        await callApi(
          `${process.env.REACT_APP_API_URL}/api/subscriptions/${sub.stripe_subscription_id}/${action}`,
          token
        );
      } else {
        // No real Stripe sub (manual/test) — update Supabase directly
        let updates = {};
        if (action === 'cancel') updates = { status: 'cancelled' };
        if (action === 'pause') updates = { status: 'paused', paused_at: new Date().toISOString() };
        if (action === 'skip') {
          const nextDate = getNextBillingDate(
            sub.current_period_end || new Date(),
            sub.product?.billing_interval || 'month'
          );
          updates = { next_payment_skipped: true, current_period_end: nextDate.toISOString() };
        }
        await supabase.from('subscriptions').update(updates).eq('id', sub.id).eq('coach_id', profile.id);
      }

      fetchSubscriptions();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActioning(null);
    }
  };

  const handleResume = async (sub) => {
    setActioning(sub.id + '_resume');
    try {
      const token = await getToken();
      const hasRealStripeId = sub.stripe_subscription_id && !sub.stripe_subscription_id.startsWith('sub_test_');

      if (hasRealStripeId) {
        await callApi(
          `${process.env.REACT_APP_API_URL}/api/subscriptions/${sub.stripe_subscription_id}/resume`,
          token
        );
      } else {
        await supabase.from('subscriptions').update({ status: 'active', paused_at: null }).eq('id', sub.id);
      }

      fetchSubscriptions();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActioning(null);
    }
  };

  const handleUpdateBillingDate = async (sub) => {
    if (!newBillingDate) return;
    setActioning(sub.id + '_billing');
    try {
      const token = await getToken();
      const hasRealStripeId = sub.stripe_subscription_id && !sub.stripe_subscription_id.startsWith('sub_test_');

      if (hasRealStripeId) {
        // Backend updates Stripe trial_end then Supabase — throws on Stripe error
        await callApi(
          `${process.env.REACT_APP_API_URL}/api/subscriptions/${sub.stripe_subscription_id}/billing-date`,
          token,
          { newDate: newBillingDate }
        );
      } else {
        await supabase.from('subscriptions')
          .update({ current_period_end: new Date(newBillingDate).toISOString() })
          .eq('id', sub.id);
      }

      setEditingBilling(null);
      setNewBillingDate('');
      fetchSubscriptions();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setActioning(null);
    }
  };

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const intervalLabels = { week: 'Weekly', biweek: 'Bi-weekly', month: 'Monthly', year: 'Yearly' };

  const filtered = subscriptions.filter(s =>
    filter === 'all' ? true : s.status === filter
  );

  const counts = {
    active: subscriptions.filter(s => s.status === 'active').length,
    paused: subscriptions.filter(s => s.status === 'paused').length,
    cancelled: subscriptions.filter(s => s.status === 'cancelled').length,
    all: subscriptions.length
  };

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Subscriptions</h1>
        <p style={{ color: 'var(--text-secondary)' }}>Manage your clients' subscriptions</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'active', label: `Active (${counts.active})` },
          { key: 'paused', label: `Paused (${counts.paused})` },
          { key: 'cancelled', label: `Cancelled (${counts.cancelled})` },
          { key: 'all', label: `All (${counts.all})` }
        ].map(tab => (
          <button key={tab.key} onClick={() => setFilter(tab.key)}
            className={filter === tab.key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)' }}>Loading...</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
              {filter === 'active' ? 'No active subscriptions yet.' : 'No subscriptions found.'}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Interval</th>
                    <th>Next Billing</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(sub => (
                    <tr key={sub.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>
                          {sub.client?.full_name || sub.client_email || '—'}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {sub.client?.email || sub.client_email || ''}
                        </div>
                      </td>
                      <td style={{ fontWeight: 500 }}>{sub.product?.name || '—'}</td>
                      <td style={{ color: 'var(--accent)', fontWeight: 500 }}>
                        {sub.product?.price_amount ? fmt(sub.product.price_amount) : '—'}
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {sub.product?.billing_type === 'one_time' ? 'One-time'
                          : intervalLabels[sub.product?.billing_interval] || '—'}
                      </td>

                      {/* Editable billing date */}
                      <td style={{ fontSize: 13 }}>
                        {editingBilling === sub.id ? (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input type="date" className="input"
                              value={newBillingDate}
                              onChange={e => setNewBillingDate(e.target.value)}
                              style={{ width: 130, fontSize: 12 }} />
                            <button className="btn btn-primary btn-sm"
                              onClick={() => handleUpdateBillingDate(sub)}
                              disabled={actioning === sub.id + '_billing'}>
                              {actioning === sub.id + '_billing' ? '...' : 'Save'}
                            </button>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => { setEditingBilling(null); setNewBillingDate(''); }}>✕</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ color: sub.next_payment_skipped ? 'var(--warning)' : 'var(--text-muted)' }}>
                              {sub.current_period_end
                                ? new Date(sub.current_period_end).toLocaleDateString('en-CA')
                                : '—'}
                              {sub.next_payment_skipped && <span style={{ fontSize: 10, marginLeft: 4 }}>skipped</span>}
                            </span>
                            {(sub.status === 'active' || sub.status === 'paused') && (
                              <button onClick={() => {
                                setEditingBilling(sub.id);
                                setNewBillingDate(sub.current_period_end
                                  ? new Date(sub.current_period_end).toISOString().split('T')[0]
                                  : '');
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--accent)', padding: 0 }}>
                                edit
                              </button>
                            )}
                          </div>
                        )}
                      </td>

                      <td>
                        <span className={`badge badge-${sub.status === 'active' ? 'active' : sub.status === 'cancelled' ? 'inactive' : 'pending'}`}>
                          {sub.status}
                        </span>
                      </td>

                      <td>
                        {sub.status === 'active' && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => handleAction(sub, 'pause')} disabled={!!actioning}>Pause</button>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => handleAction(sub, 'skip')}
                              disabled={!!actioning || sub.next_payment_skipped}>
                              {sub.next_payment_skipped ? '✓ Skipped' : 'Skip Next'}
                            </button>
                            <button className="btn btn-danger btn-sm"
                              onClick={() => handleAction(sub, 'cancel')} disabled={!!actioning}>Cancel</button>
                          </div>
                        )}
                        {sub.status === 'paused' && (
                          <button className="btn btn-primary btn-sm"
                            onClick={() => handleResume(sub)} disabled={!!actioning}>Resume</button>
                        )}
                        {sub.status === 'cancelled' && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Cancelled</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
